/**
 * Pure 21 ("Очко" / Twenty-One) state helpers.
 *
 * Rules (Mongolian / 36-card standard):
 * 1. Deck: 36 cards (6, 7, 8, 9, 10, J, Q, K, A across all 4 suits).
 *    Card values:
 *    - Ace (A): 11 points (strictly 11 in Mongolian traditional rules)
 *    - King (K): 4 points
 *    - Queen (Q): 3 points
 *    - Jack (J): 2 points
 *    - 10 to 6: Face value (10, 9, 8, 7, 6 points)
 * 2. Setup:
 *    - 2 to 4 players.
 *    - One player is Banker (dealer).
 *    - Everyone starts with initial chips (e.g. 100 chips) or Tsai in Tsai mode.
 *    - At the start of a round:
 *      Banker posts the Bank (pot) - default 10 chips/Tsai or carries over.
 *      Each player (and Banker) is dealt 1 secret card.
 * 3. Betting stage ('betting'):
 *    - Each non-banker player places a bet (min 1, max min(playerChips, currentBankPot)).
 *    - "Банк цохих" (Hit the Bank): Betting the entire bank pot.
 * 4. Hitting / Standing stage ('playing'):
 *    - Players in turn order (starting after the banker) decide to:
 *      * Hit ('hit'): Draw 1 card from the deck.
 *      * Stand ('stand'): Stop drawing.
 *    - Two Aces (A + A) = "Алтан 21" (Golden 21) -> immediate auto-win (beats normal 21).
 *    - Score > 21 = "Шатах" (Bust) -> immediate loss of bet to the bank pot.
 *    - Score == 21 -> Player can declare and wait for Banker's turn.
 * 5. Banker's turn ('banker'):
 *    - Banker reveals cards.
 *    - Banker must hit on 16 or lower, must stand on 17 or higher.
 *    - If Banker busts (> 21): All remaining non-busted players win their bet!
 * 6. Resolution / Showdown ('resolution'):
 *    - If Banker does not bust:
 *      Compare each remaining player's score to Banker's score.
 *      * Player score > Banker score -> Player wins (payout 1:1 from bank pot).
 *      * Player score < Banker score -> Player loses bet (goes to bank pot).
 *      * Player score == Banker score -> Banker wins tie ("Тэнцээнд Банкир хожно").
 *      * Special: "Алтан 21" (Two Aces) beats 21. If both have Golden 21, Banker wins tie.
 * 7. Bank rotation & Match end:
 *    - If the Bank pot drops to 0 ("Банк хагарах"):
 *      Bank rotates to the next active player.
 *    - Banker holds the bank for at most 3 consecutive rounds, then rotates.
 *    - When bank rotates around all players once, or a player runs out of chips, match completes.
 */
import {
  type Card,
  type Rank,
  type Rng,
  SUITS,
  rankOf,
  shuffle,
} from './cards.js';
import type { GameFormat, SeatCount, GameView, TwentyOneView, SeatAction } from './protocol.js';
import type { GameState, Result, ViewExtras } from './game.js';

export const TWENTYONE_RANKS: readonly Rank[] = [
  '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'
] as const;

export const TWENTYONE_REMOVED: Card[] = (() => {
  const removed: Card[] = [];
  for (const r of ['2', '3', '4', '5'] as const) {
    for (const s of SUITS) removed.push(r + s);
  }
  return removed;
})();

export function twentyOneDeck(): Card[] {
  const deck: Card[] = [];
  for (const r of TWENTYONE_RANKS) {
    for (const s of SUITS) {
      deck.push(r + s);
    }
  }
  return deck;
}

export function cardPointValue(card: Card): number {
  const r = rankOf(card);
  switch (r) {
    case 'A': return 11;
    case 'K': return 4;
    case 'Q': return 3;
    case 'J': return 2;
    case 'T': return 10;
    case '9': return 9;
    case '8': return 8;
    case '7': return 7;
    case '6': return 6;
    default: return 0;
  }
}

export function isGolden21(hand: readonly Card[]): boolean {
  return hand.length === 2 && rankOf(hand[0]) === 'A' && rankOf(hand[1]) === 'A';
}

export function handScore(hand: readonly Card[]): number {
  if (isGolden21(hand)) {
    return 21; // Treated as 21 with special priority
  }
  let total = 0;
  for (const c of hand) {
    total += cardPointValue(c);
  }
  return total;
}

export interface TwentyOneState {
  stage: 'betting' | 'playing' | 'banker' | 'finished';
  bankerSeat: number;
  bankPot: number;
  initialBankerPot: number;
  bankerRoundsHeld: number;
  deck: Card[];
  bets: number[];
  busted: boolean[];
  stood: boolean[];
  golden21: boolean[];
  scores: number[]; // Player chips/totals
  roundNetTransfers: number[]; // Net chip changes this round
}

export function twentyOneFormat(seats: SeatCount): GameFormat {
  return {
    seats,
    cardsEach: 1,
    passesToClear: Math.max(1, seats - 1),
    removed: [...TWENTYONE_REMOVED],
  };
}

export const TWENTYONE_DEFAULT_CHIPS = 100;
export const TWENTYONE_DEFAULT_BANK_POT = 20;

export function createTwentyOneGame(
  playerIds: string[],
  rng: Rng = Math.random,
  options?: {
    dealerSeat?: number;
    twentyOneScores?: number[];
    bankPot?: number;
    bankerRoundsHeld?: number;
  }
): GameState {
  const seats = playerIds.length as SeatCount;
  if (seats < 2 || seats > 4) {
    throw new Error(`Unsupported 21 seat count: ${playerIds.length}`);
  }
  const format = twentyOneFormat(seats);
  const deck = shuffle(twentyOneDeck(), rng);

  const bankerSeat = options?.dealerSeat ?? seats - 1;
  const scores = options?.twentyOneScores ?? playerIds.map(() => TWENTYONE_DEFAULT_CHIPS);
  const bankerRoundsHeld = options?.bankerRoundsHeld ?? 0;

  // Banker contributes bankPot from their score into the bank pool
  let bankPot = options?.bankPot ?? TWENTYONE_DEFAULT_BANK_POT;
  bankPot = Math.min(bankPot, scores[bankerSeat]);
  scores[bankerSeat] -= bankPot;

  // Deal 1 secret card to each player and banker
  const hands: Card[][] = [];
  for (let i = 0; i < seats; i++) {
    hands.push([deck[i]]);
  }
  const remainingDeck = deck.slice(seats);

  // Turn starts with the first non-banker seat for betting
  const firstBettor = (bankerSeat + 1) % seats;

  const bets = playerIds.map(() => 0);
  const busted = playerIds.map(() => false);
  const stood = playerIds.map(() => false);
  const golden21 = playerIds.map((_, i) => isGolden21(hands[i]));

  return {
    gameType: 'twentyone',
    format,
    playerIds: [...playerIds],
    hands,
    dead: [],
    phase: 'playing',
    turn: firstBettor,
    trick: null,
    passes: 0,
    lastActions: playerIds.map(() => null),
    mustInclude: null,
    winner: null,
    twentyone: {
      stage: 'betting',
      bankerSeat,
      bankPot,
      initialBankerPot: bankPot,
      bankerRoundsHeld,
      deck: remainingDeck,
      bets,
      busted,
      stood,
      golden21,
      scores,
      roundNetTransfers: playerIds.map(() => 0),
    },
  };
}

export function nextBettorSeat(state: GameState, currentSeat: number): number | null {
  const { bankerSeat } = state.twentyone!;
  const seats = state.format.seats;
  for (let step = 1; step < seats; step++) {
    const next = (currentSeat + step) % seats;
    if (next !== bankerSeat && state.twentyone!.bets[next] === 0) {
      return next;
    }
  }
  return null;
}

export function nextPlayerSeat(state: GameState, currentSeat: number): number | null {
  const { bankerSeat, stood, busted } = state.twentyone!;
  const seats = state.format.seats;
  for (let step = 1; step < seats; step++) {
    const next = (currentSeat + step) % seats;
    if (next !== bankerSeat && !stood[next] && !busted[next]) {
      return next;
    }
  }
  return null;
}

/**
 * Executes a bet for the player in the betting stage.
 * In wire protocol, bets can be submitted via PLAY_CARDS with custom payload or numeric amount.
 */
export function applyTwentyOneBet(state: GameState, seat: number, amount: number): Result {
  const t = state.twentyone;
  if (!t || t.stage !== 'betting') {
    return { ok: false, error: 'Not in betting stage.' };
  }
  if (state.turn !== seat) {
    return { ok: false, error: 'It is not your turn to bet.' };
  }
  if (seat === t.bankerSeat) {
    return { ok: false, error: 'Banker does not place a player bet.' };
  }

  const maxBet = Math.min(t.scores[seat], t.bankPot);
  if (amount < 1 || amount > maxBet) {
    return { ok: false, error: `Bet must be between 1 and ${maxBet}.` };
  }

  const nextScores = [...t.scores];
  nextScores[seat] -= amount;
  const nextBets = [...t.bets];
  nextBets[seat] = amount;

  const nextBettor = nextBettorSeat({ ...state, twentyone: { ...t, bets: nextBets } }, seat);

  if (nextBettor !== null) {
    // Next player's betting turn
    const nextState: GameState = {
      ...state,
      turn: nextBettor,
      twentyone: {
        ...t,
        bets: nextBets,
        scores: nextScores,
      },
    };
    return {
      ok: true,
      state: nextState,
      event: { type: 'play', seat, combo: { type: 'single', cards: [], key: [0, 0] }, remaining: state.hands[seat].length, won: false },
    };
  }

  // All non-bankers have placed bets! Advance to 'playing' stage (hitting/standing).
  const firstPlayer = (t.bankerSeat + 1) % state.format.seats;
  const nextState: GameState = {
    ...state,
    turn: firstPlayer,
    twentyone: {
      ...t,
      stage: 'playing',
      bets: nextBets,
      scores: nextScores,
    },
  };

  return {
    ok: true,
    state: nextState,
    event: { type: 'play', seat, combo: { type: 'single', cards: [], key: [0, 0] }, remaining: state.hands[seat].length, won: false },
  };
}

/**
 * Player action during hitting / standing stage.
 * Action: 'hit' (draw card) or 'stand' (stop drawing).
 */
export function applyTwentyOneHit(state: GameState, seat: number): Result {
  const t = state.twentyone;
  if (!t || (t.stage !== 'playing' && t.stage !== 'banker')) {
    return { ok: false, error: 'Not your turn to draw cards.' };
  }
  if (state.turn !== seat) {
    return { ok: false, error: 'It is not your turn.' };
  }
  if (t.busted[seat] || t.stood[seat]) {
    return { ok: false, error: 'You have already completed your turn.' };
  }
  if (t.deck.length === 0) {
    return { ok: false, error: 'No more cards in the deck.' };
  }

  const drawnCard = t.deck[0];
  const nextDeck = t.deck.slice(1);
  const nextHand = [...state.hands[seat], drawnCard];
  const nextHands = state.hands.map((h, i) => (i === seat ? nextHand : h));

  const score = handScore(nextHand);
  const isGolden = isGolden21(nextHand);
  const isBust = score > 21 && !isGolden;

  const nextBusted = [...t.busted];
  const nextStood = [...t.stood];
  const nextGolden = [...t.golden21];

  if (isBust) {
    nextBusted[seat] = true;
  }
  if (isGolden) {
    nextGolden[seat] = true;
    nextStood[seat] = true; // Auto-stand on Golden 21
  } else if (score === 21) {
    nextStood[seat] = true; // Auto-stand on exact 21
  }

  // If this was the banker playing:
  if (seat === t.bankerSeat) {
    const updatedState: GameState = {
      ...state,
      hands: nextHands,
      twentyone: {
        ...t,
        deck: nextDeck,
        busted: nextBusted,
        stood: nextStood,
        golden21: nextGolden,
      },
    };
    if (isBust || score >= 17) {
      // Banker must stand on 17+ or bust -> finish round resolution!
      return resolveTwentyOneRound(updatedState);
    }
    // Banker needs to keep drawing (<= 16)
    return {
      ok: true,
      state: updatedState,
      event: { type: 'play', seat, combo: { type: 'single', cards: [drawnCard], key: [0, 0] }, remaining: nextHand.length, won: false },
    };
  }

  // Player drew card:
  const turnFinished = nextBusted[seat] || nextStood[seat];
  if (!turnFinished) {
    // Player continues their turn
    const nextState: GameState = {
      ...state,
      hands: nextHands,
      twentyone: {
        ...t,
        deck: nextDeck,
        busted: nextBusted,
        stood: nextStood,
        golden21: nextGolden,
      },
    };
    return {
      ok: true,
      state: nextState,
      event: { type: 'play', seat, combo: { type: 'single', cards: [drawnCard], key: [0, 0] }, remaining: nextHand.length, won: false },
    };
  }

  // Player's turn is done, find next player or advance to Banker's turn
  const nextPlayer = nextPlayerSeat({ ...state, twentyone: { ...t, stood: nextStood, busted: nextBusted } }, seat);

  if (nextPlayer !== null) {
    const nextState: GameState = {
      ...state,
      turn: nextPlayer,
      hands: nextHands,
      twentyone: {
        ...t,
        deck: nextDeck,
        busted: nextBusted,
        stood: nextStood,
        golden21: nextGolden,
      },
    };
    return {
      ok: true,
      state: nextState,
      event: { type: 'play', seat, combo: { type: 'single', cards: [drawnCard], key: [0, 0] }, remaining: nextHand.length, won: false },
    };
  }

  // All players finished -> Banker's turn
  return startBankerTurn({
    ...state,
    hands: nextHands,
    twentyone: {
      ...t,
      deck: nextDeck,
      busted: nextBusted,
      stood: nextStood,
      golden21: nextGolden,
    },
  });
}

export function applyTwentyOneStand(state: GameState, seat: number): Result {
  const t = state.twentyone;
  if (!t || (t.stage !== 'playing' && t.stage !== 'banker')) {
    return { ok: false, error: 'Not in playing or banker stage.' };
  }
  if (state.turn !== seat) {
    return { ok: false, error: 'It is not your turn.' };
  }
  if (seat === t.bankerSeat) {
    const bankerScore = handScore(state.hands[seat]);
    if (bankerScore < 17 && !isGolden21(state.hands[seat])) {
      return { ok: false, error: 'Banker must draw on 16 or lower.' };
    }
    const nextStood = [...t.stood];
    nextStood[seat] = true;
    return resolveTwentyOneRound({
      ...state,
      twentyone: {
        ...t,
        stood: nextStood,
      },
    });
  }

  const nextStood = [...t.stood];
  nextStood[seat] = true;

  const nextPlayer = nextPlayerSeat({ ...state, twentyone: { ...t, stood: nextStood } }, seat);

  if (nextPlayer !== null) {
    const nextState: GameState = {
      ...state,
      turn: nextPlayer,
      twentyone: {
        ...t,
        stood: nextStood,
      },
    };
    return {
      ok: true,
      state: nextState,
      event: { type: 'pass', seat, trickCleared: false, nextLead: nextPlayer },
    };
  }

  // All players stood or busted -> Advance to Banker's turn
  return startBankerTurn({
    ...state,
    twentyone: {
      ...t,
      stood: nextStood,
    },
  });
}

function startBankerTurn(state: GameState): Result {
  const t = state.twentyone!;
  const bankerSeat = t.bankerSeat;
  const bankerHand = state.hands[bankerSeat];
  const bankerScore = handScore(bankerHand);
  const isGolden = isGolden21(bankerHand);

  // If all players busted, banker doesn't even need to draw; immediately resolves
  const allPlayersBusted = state.playerIds.every((_, i) => i === bankerSeat || t.busted[i]);
  if (allPlayersBusted) {
    return resolveTwentyOneRound({
      ...state,
      turn: bankerSeat,
      twentyone: {
        ...t,
        stage: 'banker',
      },
    });
  }

  // If Banker has Golden 21 or score >= 17, banker stands immediately
  if (isGolden || bankerScore >= 17) {
    const nextStood = [...t.stood];
    nextStood[bankerSeat] = true;
    return resolveTwentyOneRound({
      ...state,
      turn: bankerSeat,
      twentyone: {
        ...t,
        stage: 'banker',
        stood: nextStood,
        golden21: nextGolden(t.golden21, bankerSeat, isGolden),
      },
    });
  }

  // Banker must hit (score <= 16)
  const nextState: GameState = {
    ...state,
    turn: bankerSeat,
    twentyone: {
      ...t,
      stage: 'banker',
    },
  };

  return {
    ok: true,
    state: nextState,
    event: { type: 'play', seat: bankerSeat, combo: { type: 'single', cards: [], key: [0, 0] }, remaining: bankerHand.length, won: false },
  };
}

function nextGolden(arr: readonly boolean[], seat: number, val: boolean): boolean[] {
  const c = [...arr];
  c[seat] = val;
  return c;
}

/**
 * Resolves the round after Banker stands or busts.
 * Evaluates winners and transfers from/to the bank pot.
 */
export function resolveTwentyOneRound(state: GameState): Result {
  const t = state.twentyone!;
  const bankerSeat = t.bankerSeat;
  const bankerHand = state.hands[bankerSeat];
  const bankerScore = handScore(bankerHand);
  const bankerGolden = isGolden21(bankerHand);
  const bankerBust = bankerScore > 21 && !bankerGolden;

  let currentPot = t.bankPot;
  const netTransfers = state.playerIds.map(() => 0);
  const updatedScores = [...t.scores];

  for (let i = 0; i < state.format.seats; i++) {
    if (i === bankerSeat) continue;
    const bet = t.bets[i];
    if (bet <= 0) continue;

    const pHand = state.hands[i];
    const pScore = handScore(pHand);
    const pGolden = isGolden21(pHand);
    const pBust = t.busted[i];

    if (pBust) {
      // Player busted: lost bet directly to bank pot
      currentPot += bet;
      netTransfers[i] = -bet;
    } else if (bankerBust) {
      // Banker busted, player did not: player wins 1:1
      const winAmount = Math.min(bet, currentPot);
      currentPot -= winAmount;
      updatedScores[i] += bet + winAmount;
      netTransfers[i] = winAmount;
    } else {
      // Showdown comparison:
      // Golden 21 beats normal 21
      let playerWins = false;
      if (pGolden && !bankerGolden) {
        playerWins = true;
      } else if (!pGolden && bankerGolden) {
        playerWins = false; // Banker Golden 21 wins
      } else if (pGolden && bankerGolden) {
        playerWins = false; // Tie: Banker wins!
      } else if (pScore > bankerScore) {
        playerWins = true;
      } else {
        // pScore <= bankerScore -> Banker wins tie or higher score
        playerWins = false;
      }

      if (playerWins) {
        const winAmount = Math.min(bet, currentPot);
        currentPot -= winAmount;
        updatedScores[i] += bet + winAmount;
        netTransfers[i] = winAmount;
      } else {
        // Player loses bet to pot
        currentPot += bet;
        netTransfers[i] = -bet;
      }
    }
  }

  // Net change for banker is pot difference
  netTransfers[bankerSeat] = currentPot - t.initialBankerPot;
  updatedScores[bankerSeat] += currentPot;

  // Determine round winner (who won the most net chips this round)
  let bestWinner = bankerSeat;
  let maxNet = -Infinity;
  for (let i = 0; i < state.format.seats; i++) {
    if (netTransfers[i] > maxNet) {
      maxNet = netTransfers[i];
      bestWinner = i;
    }
  }

  const finishedState: GameState = {
    ...state,
    phase: 'finished',
    turn: null,
    winner: bestWinner,
    twentyone: {
      ...t,
      stage: 'finished',
      bankPot: currentPot,
      bankerRoundsHeld: t.bankerRoundsHeld + 1,
      scores: updatedScores,
      roundNetTransfers: netTransfers,
    },
  };

  return {
    ok: true,
    state: finishedState,
    event: { type: 'play', seat: bankerSeat, combo: { type: 'single', cards: [], key: [0, 0] }, remaining: bankerHand.length, won: true },
  };
}

export function applyTwentyOneTimeout(state: GameState): Result {
  const t = state.twentyone;
  if (!t || state.turn === null) {
    return { ok: false, error: 'No active turn.' };
  }
  const seat = state.turn;

  if (t.stage === 'betting') {
    const maxBet = Math.min(t.scores[seat], t.bankPot);
    if (maxBet <= 0) {
      const nextStood = [...t.stood];
      nextStood[seat] = true;
      const nextBettor = nextBettorSeat(state, seat);
      if (nextBettor !== null) {
        return {
          ok: true,
          state: {
            ...state,
            turn: nextBettor,
            twentyone: { ...t, stood: nextStood },
          },
          event: { type: 'play', seat, combo: { type: 'single', cards: [], key: [0, 0] }, remaining: state.hands[seat].length, won: false },
        };
      }
      return {
        ok: true,
        state: {
          ...state,
          turn: (t.bankerSeat + 1) % state.format.seats,
          twentyone: { ...t, stage: 'playing', stood: nextStood },
        },
        event: { type: 'play', seat, combo: { type: 'single', cards: [], key: [0, 0] }, remaining: state.hands[seat].length, won: false },
      };
    }
    const minBet = 1;
    return applyTwentyOneBet(state, seat, minBet);
  }

  if (t.stage === 'playing') {
    // Timeout during player's turn: stand
    return applyTwentyOneStand(state, seat);
  }

  if (t.stage === 'banker') {
    // Banker must hit if <= 16, stand if >= 17
    const bankerScore = handScore(state.hands[seat]);
    if (bankerScore <= 16) {
      return applyTwentyOneHit(state, seat);
    }
    return resolveTwentyOneRound(state);
  }

  return { ok: false, error: 'Invalid stage for timeout.' };
}

export function twentyOneViewFor(state: GameState, seat: number, extras: ViewExtras): GameView {
  const id = (s: number) => state.playerIds[s];
  const lastActions: Record<string, SeatAction> = {};
  state.lastActions.forEach((a, s) => {
    if (a) lastActions[id(s)] = a.type === 'play' ? { type: 'play', cards: [...a.cards] } : { type: 'pass' };
  });

  let penalties: Record<string, number> | null = null;
  let scoreDeltas: Record<string, number> | null = null;
  if (state.phase === 'finished') {
    penalties = {};
    const t = state.twentyone;
    if (t) {
      t.roundNetTransfers.forEach((net, s) => (penalties![id(s)] = net < 0 ? -net : 0));
    } else {
      state.playerIds.forEach((pid) => (penalties![pid] = 0));
    }
    scoreDeltas = {};
    if (t) {
      t.roundNetTransfers.forEach((net, s) => (scoreDeltas![id(s)] = net));
    } else {
      state.playerIds.forEach((pid) => (scoreDeltas![pid] = 0));
    }
  }

  let twentyoneView: TwentyOneView | undefined;
  if (state.twentyone) {
    const t = state.twentyone;
    const bets: Record<string, number> = {};
    const scores: Record<string, number> = {};
    const roundNetTransfers: Record<string, number> = {};
    t.bets.forEach((bet, s) => {
      bets[id(s)] = bet;
    });
    t.scores.forEach((score, s) => {
      scores[id(s)] = score;
    });
    t.roundNetTransfers.forEach((net, s) => {
      roundNetTransfers[id(s)] = net;
    });
    const bustedPlayerIds = t.busted.map((b, s) => (b ? id(s) : null)).filter(Boolean) as string[];
    const stoodPlayerIds = t.stood.map((st, s) => (st ? id(s) : null)).filter(Boolean) as string[];
    const golden21PlayerIds = t.golden21.map((g, s) => (g ? id(s) : null)).filter(Boolean) as string[];

    const revealBanker = t.stage === 'banker' || t.stage === 'finished';
    const bankerSeat = t.bankerSeat;

    twentyoneView = {
      stage: t.stage,
      bankerSeat,
      bankPot: t.bankPot,
      bets,
      bustedPlayerIds,
      stoodPlayerIds,
      golden21PlayerIds,
      scores,
      myScore: handScore(state.hands[seat]),
      ...(revealBanker
        ? {
            bankerHand: [...state.hands[bankerSeat]],
            bankerScore: handScore(state.hands[bankerSeat]),
          }
        : {}),
      ...(t.stage === 'finished' ? { roundNetTransfers } : {}),
    };
  }

  return {
    roomId: extras.roomId,
    gameType: 'twentyone',
    phase: state.phase,
    format: { ...state.format, removed: [...state.format.removed] },
    myPlayerId: id(seat),
    mySeat: seat,
    myHand: extras.pause?.status === 'paused' ? [] : [...state.hands[seat]],
    players: extras.players.map((p) => ({
      ...p,
      cardCount: state.hands[p.seat].length,
    })),
    activePlayerId: state.turn === null ? null : id(state.turn),
    timeoutEndsAt: extras.timeoutEndsAt,
    ...(extras.turnDurationMs !== undefined ? { turnDurationMs: extras.turnDurationMs } : {}),
    pause: extras.pause ?? null,
    trick: null,
    lastActions,
    mustInclude: null,
    winnerId: state.winner === null ? null : id(state.winner),
    penalties,
    totals: { ...extras.totals },
    ...(extras.serverTime !== undefined ? { serverTime: extras.serverTime } : {}),
    ...(twentyoneView ? { twentyone: twentyoneView } : {}),
  };
}
